import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateNeedFormDto,
  CreateNeedFormSchema,
  FilterOptionDto,
  NeedDto,
  NeedsCategoryDto,
  NeedsIssueDto,
  SuggestedNeedDto,
  UpdateNeedFormDto,
  UpdateNeedFormSchema,
} from '@act/shared/data-transfer-objects';
import { useApiCall } from 'redux/utils/api-hook';
import { needRequests } from 'redux/requests/need.requests';
import { useToast } from '@act/react-components/app/ReusableToast';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import {
  selectNeedsCategoryByIdFromMetadata,
  selectNeedsIssueByIdFromMetadata,
  selectNeedsMetadata,
  selectNeedsZcodeByIdFromMetadata,
} from 'redux/slices/metaSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { createNeedSuccess, patchNeedSuccess } from 'redux/slices/needs.slice';
import { selectSelectedMemberId } from 'redux/slices/selectedMemberSlice';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { setSidePanelIsDirty } from '@act/react-components/member-page/Calls/FullScreenSaveClose.utils';
import { NEED_REASON_TEMPLATE } from '@act/react-components/needs/constants';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';

interface UseAddNeedFormProps {
  need?: NeedDto;
  suggestedNeed?: SuggestedNeedDto;
  onClose: () => void;
  trackOpenState?: boolean;
}

export const useAddNeedForm = ({
  need,
  suggestedNeed,
  onClose,
  trackOpenState = false,
}: UseAddNeedFormProps) => {
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const needsDescriptionFF = useFeatureFlag('needs-default-description');
  const needsMetadata = useAppSelector(selectNeedsMetadata);
  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const needIssueOptions = metaSliceSelectors.useSelectNeedIssueOptions();
  const [openLearnMoreContent, setOpenLearnMoreContent] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<NeedsCategoryDto>(null);
  const [selectedIssue, setSelectedIssue] = useState<NeedsIssueDto>(null);
  const [isClosing, setIsClosing] = useState(false);
  const hasClosedRef = useRef(false);
  const [createdNeedResult, isCreatingNeed, createNeed, createNeedError] =
    useApiCall(needRequests.createNeed);
  const [updateNeedResult, isPatchingNeed, patchNeed, patchNeedError] =
    useApiCall(needRequests.patchNeed);
  const isSubmitting = isCreatingNeed || isPatchingNeed;
  const defaultReason = needsDescriptionFF
    ? NEED_REASON_TEMPLATE
    : '<p><br></p>';

  const { control, watch, formState, reset, setValue, handleSubmit } = useForm<
    CreateNeedFormDto | UpdateNeedFormDto
  >({
    mode: 'onChange',
    resolver: zodResolver(need ? UpdateNeedFormSchema : CreateNeedFormSchema),
    defaultValues: {
      issueId: need?.issueId ?? suggestedNeed?.issueId ?? 0,
      reason: need?.reason ?? suggestedNeed?.reason ?? defaultReason,
    },
  });
  const { isDirty } = formState;
  const watchIssue = watch('issueId');

  useEffect(() => {
    if (trackOpenState) {
      setSidePanelIsDirty(isDirty);
    }
  }, [isDirty, trackOpenState]);

  useEffect(() => {
    if (!trackOpenState) {
      setSidePanelIsDirty(false);
    }
    return () => {
      setSidePanelIsDirty(false);
    };
  }, [trackOpenState]);

  const selectedIssueZCode = selectNeedsZcodeByIdFromMetadata(
    needsMetadata?.data,
    selectedIssue?.zcodeId
  );

  useEffect(() => {
    if (watchIssue) {
      const issue = selectNeedsIssueByIdFromMetadata(
        needsMetadata?.data,
        watchIssue
      );
      const category = selectNeedsCategoryByIdFromMetadata(
        needsMetadata?.data,
        issue?.categoryId
      );
      setSelectedIssue(issue);
      setSelectedCategory(category);
    }
  }, [watchIssue, needsMetadata?.data]);

  useEffect(() => {
    if (!isClosing && need) {
      reset({
        issueId: need.issueId,
        reason: need.reason,
      });
    } else if (!isClosing && suggestedNeed) {
      reset({
        issueId: suggestedNeed.issueId,
        reason: suggestedNeed.reason,
      });
    }
  }, [need?.id, suggestedNeed?.issueId, reset, isClosing]);

  useEffect(() => {
    if ((createdNeedResult || updateNeedResult) && !hasClosedRef.current) {
      hasClosedRef.current = true;
      setIsClosing(true);
      if (createdNeedResult) {
        dispatch(createNeedSuccess(createdNeedResult));
        toast('Need created successfully');
      }
      if (updateNeedResult) {
        dispatch(patchNeedSuccess(updateNeedResult));
        toast('Need updated successfully');
      }
      setSidePanelIsDirty(false);
      
      // Use setTimeout to ensure Redux updates complete before closing
      setTimeout(() => {
        onClose();
      }, 0);
    }
  }, [createdNeedResult, updateNeedResult, dispatch, toast, onClose]);

  useEffect(() => {
    if (createNeedError || patchNeedError) {
      const userMessage =
        createNeedError?.userMessage || patchNeedError?.userMessage;
      toast(
        userMessage
          ? `Failed to create or update need. Please try again. ${userMessage}`
          : 'Failed to create or update need. Please try again.',
        'error'
      );
    }
  }, [createNeedError, patchNeedError, toast]);

  async function handleSubmission(data: CreateNeedFormDto | UpdateNeedFormDto) {
    if (isSubmitting) return;
    if (need) {
      patchNeed({
        body: {
          issueId: data.issueId,
          reason: data.reason,
        },
        params: { needId: need.id },
      });
    } else {
      createNeed({
        body: {
          memberId: selectedMemberId,
          issueId: data.issueId,
          reason: data.reason,
          ...(!!suggestedNeed &&
            !!suggestedNeed.tempNeedId && {
              tempNeedId: suggestedNeed.tempNeedId,
            }),
        },
        params: null,
      });
    }
  }

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  const getFormConfig = useCallback(
    (
      alertContent: React.ReactNode,
      textContent: React.ReactNode,
      inputAdornment: React.ReactElement
    ): FormConfig<CreateNeedFormDto | UpdateNeedFormDto> => {
      return [
        {
          type: 'input' as const,
          name: 'issueId' as const,
          label: 'Search for need',
          input: 'autocomplete' as const,
          props: {
            options: needIssueOptions,
            getOptionLabel: (option: FilterOptionDto) => option?.label || '',
            required: true,
            groupBy: (option: FilterOptionDto) => option?.tagCloud[0],
            startAdornment: inputAdornment,
            trackingName: 'create-need-dialog-issue-autocomplete',
            sensitive: false,
          },
        },
        {
          type: 'passthrough' as const,
          content: alertContent,
        },
        {
          type: 'passthrough' as const,
          content: textContent,
        },
        {
          type: 'input' as const,
          name: 'reason' as const,
          label: 'Reason for pursuing this need (required)',
          input: 'richText' as const,
          props: {
            placeholderText: needsDescriptionFF
              ? defaultReason
              : 'Reason for pursuing this need (required)',
            height: 346,
            required: true,
          },
        },
      ];
    },
    [
      needIssueOptions,
      needsDescriptionFF,
      defaultReason,
    ]
  );

  return {
    control,
    watch,
    formState,
    reset,
    setValue,
    handleSubmit,
    isDirty,
    isSubmitting,
    handleSubmission,
    handleClose,
    needsDescriptionFF,
    defaultReason,
    selectedCategory,
    selectedIssue,
    selectedIssueZCode,
    openLearnMoreContent,
    setOpenLearnMoreContent,
    needIssueOptions,
    getFormConfig,
  };
};
